import { normalizeMultiValue } from './financialOnboardingStorage';

export const COMPANY_ONBOARDING_PROFILE_FIELDS = [
  'accountPurpose',
  'accountPurposeOther',
  'sourceOfFunds',
  'sourceOfFundsOther',
  'isDao',
  'customerType',
  'operatingCountries',
  'companySize',
  'annualRevenue',
  'monthlySendVolume',
  'monthlyPaymentCount',
  'highestSingleTransfer',
  'destinationCountries',
  'isSanctioned',
  'sanctionsDetail',
  'hasAccountRestricted',
  'accountRestrictedDetail',
  'operatesInProhibitedCountries',
  'highRiskActivities',
  'conductsMoneyServices',
  'conductsMoneyServicesDescription',
  'actingAsIntermediary',
  'sourceOfFundsDescription',
];

function getCompanyOnboardingProfile(user) {
  return user?.company?.onboardingProfile ?? user?.company?.OnboardingProfile ?? {};
}

function getProfileValue(profile, camelCaseField, pascalCaseField) {
  return profile?.[camelCaseField] ?? profile?.[pascalCaseField];
}

function hasBooleanValue(value) {
  return typeof value === 'boolean';
}

function hasTextValue(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizeProfileInput(profile) {
  return {
    ...profile,
    accountPurpose: profile.accountPurpose ?? profile.AccountPurpose ?? null,
    accountPurposeOther: profile.accountPurposeOther ?? profile.AccountPurposeOther ?? null,
    sourceOfFunds: profile.sourceOfFunds ?? profile.SourceOfFunds ?? null,
    sourceOfFundsOther: profile.sourceOfFundsOther ?? profile.SourceOfFundsOther ?? null,
    isDao: profile.isDao ?? profile.IsDao ?? null,
    isSanctioned: profile.isSanctioned ?? profile.IsSanctioned ?? null,
    sanctionsDetail: profile.sanctionsDetail ?? profile.SanctionsDetail ?? null,
    hasAccountRestricted: profile.hasAccountRestricted ?? profile.HasAccountRestricted ?? null,
    accountRestrictedDetail: profile.accountRestrictedDetail ?? profile.AccountRestrictedDetail ?? null,
    operatesInProhibitedCountries: profile.operatesInProhibitedCountries ?? profile.OperatesInProhibitedCountries ?? null,
    highRiskActivities: profile.highRiskActivities ?? profile.HighRiskActivities ?? null,
    conductsMoneyServices: profile.conductsMoneyServices ?? profile.ConductsMoneyServices ?? null,
    conductsMoneyServicesDescription:
      profile.conductsMoneyServicesDescription ?? profile.ConductsMoneyServicesDescription ?? null,
    actingAsIntermediary: profile.actingAsIntermediary ?? profile.ActingAsIntermediary ?? null,
    sourceOfFundsDescription: profile.sourceOfFundsDescription ?? profile.SourceOfFundsDescription ?? null,
  };
}

export function buildCompanyOnboardingProfilePayload(profile) {
  const normalizedProfile = normalizeProfileInput(profile);
  return COMPANY_ONBOARDING_PROFILE_FIELDS.reduce(
    (payload, field) => ({
      ...payload,
      [field]: normalizedProfile[field] ?? null,
    }),
    {},
  );
}

export function isCompanyActivityFirstScreenComplete(user) {
  const profile = getCompanyOnboardingProfile(user);

  return Boolean(
    getProfileValue(profile, 'activityFirstScreenCompleted', 'ActivityFirstScreenCompleted') ||
      (getProfileValue(profile, 'customerType', 'CustomerType') &&
        normalizeMultiValue(getProfileValue(profile, 'operatingCountries', 'OperatingCountries')).length > 0 &&
        getProfileValue(profile, 'companySize', 'CompanySize') &&
        getProfileValue(profile, 'annualRevenue', 'AnnualRevenue')),
  );
}

export function isCompanyActivitySecondScreenComplete(user) {
  const profile = getCompanyOnboardingProfile(user);

  return Boolean(
    getProfileValue(profile, 'activitySecondScreenCompleted', 'ActivitySecondScreenCompleted') ||
      (getProfileValue(profile, 'monthlySendVolume', 'MonthlySendVolume') &&
        getProfileValue(profile, 'monthlyPaymentCount', 'MonthlyPaymentCount') &&
        getProfileValue(profile, 'highestSingleTransfer', 'HighestSingleTransfer') &&
        normalizeMultiValue(getProfileValue(profile, 'destinationCountries', 'DestinationCountries')).length > 0),
  );
}

export function isCompanyActivityOnboardingComplete(user) {
  return isCompanyActivityFirstScreenComplete(user) && isCompanyActivitySecondScreenComplete(user);
}

export function isCompanyRiskOnboardingComplete(user) {
  const profile = normalizeProfileInput(getCompanyOnboardingProfile(user));
  const isSanctioned = getProfileValue(profile, 'isSanctioned', 'IsSanctioned');
  const sanctionsDetail = getProfileValue(profile, 'sanctionsDetail', 'SanctionsDetail');
  const hasAccountRestricted = getProfileValue(profile, 'hasAccountRestricted', 'HasAccountRestricted');
  const accountRestrictedDetail = getProfileValue(profile, 'accountRestrictedDetail', 'AccountRestrictedDetail');
  const operatesInProhibitedCountries = getProfileValue(profile, 'operatesInProhibitedCountries', 'OperatesInProhibitedCountries');
  const highRiskActivities = getProfileValue(profile, 'highRiskActivities', 'HighRiskActivities');
  const conductsMoneyServices = getProfileValue(profile, 'conductsMoneyServices', 'ConductsMoneyServices');
  const conductsMoneyServicesDescription = getProfileValue(
    profile,
    'conductsMoneyServicesDescription',
    'ConductsMoneyServicesDescription',
  );
  const actingAsIntermediary = getProfileValue(profile, 'actingAsIntermediary', 'ActingAsIntermediary');
  const hasActivities =
    typeof highRiskActivities === 'string'
      ? highRiskActivities.trim().length > 0
      : Array.isArray(highRiskActivities) && highRiskActivities.length > 0;

  return Boolean(
    hasBooleanValue(isSanctioned) &&
      (!isSanctioned || hasTextValue(sanctionsDetail)) &&
      hasBooleanValue(hasAccountRestricted) &&
      (!hasAccountRestricted || hasTextValue(accountRestrictedDetail)) &&
      hasBooleanValue(operatesInProhibitedCountries) &&
      hasActivities &&
      hasBooleanValue(conductsMoneyServices) &&
      (!conductsMoneyServices || hasTextValue(conductsMoneyServicesDescription)) &&
      hasBooleanValue(actingAsIntermediary),
  );
}
